// Original items written for this site, inspired by the public four-axis
// personality-type framework (Extraversion/Introversion, Sensing/Intuition,
// Thinking/Feeling, Judging/Perceiving popularized by Myers-Briggs and Jung's
// typology). Not the licensed Myers-Briggs assessment or its copyrighted item
// text: this is an independent, informal self-reflection quiz.

export type MbtiPole = 'E' | 'I' | 'S' | 'N' | 'T' | 'F' | 'J' | 'P';

export interface MbtiQuestion {
  id: string;
  text: string;
  pole: MbtiPole;
}

export const MBTI_AXES: Array<[MbtiPole, MbtiPole]> = [
  ['E', 'I'],
  ['S', 'N'],
  ['T', 'F'],
  ['J', 'P'],
];

export const MBTI_POLE_LABEL: Record<MbtiPole, string> = {
  E: 'Extraversion',
  I: 'Introversion',
  S: 'Sensing',
  N: 'Intuition',
  T: 'Thinking',
  F: 'Feeling',
  J: 'Judging',
  P: 'Perceiving',
};

export const MBTI_QUESTIONS: MbtiQuestion[] = [
  // Extraversion
  { id: 'e1', text: 'I feel energized after spending time in a big group of people.', pole: 'E' },
  {
    id: 'e2',
    text: "I'd rather think out loud with someone than work through a problem alone.",
    pole: 'E',
  },
  { id: 'e3', text: "Walking into a party full of strangers doesn't faze me.", pole: 'E' },
  { id: 'e4', text: 'I do my best thinking while talking, not while sitting quietly.', pole: 'E' },
  // Introversion
  {
    id: 'i1',
    text: 'I need real alone time to recharge after being around people all day.',
    pole: 'I',
  },
  {
    id: 'i2',
    text: "I'd rather have one deep conversation than mingle with a whole room.",
    pole: 'I',
  },
  { id: 'i3', text: 'Big social events drain me faster than they excite me.', pole: 'I' },
  {
    id: 'i4',
    text: 'I prefer to think something through privately before I say it out loud.',
    pole: 'I',
  },
  // Sensing
  {
    id: 's1',
    text: 'I trust concrete facts and past experience more than a hunch.',
    pole: 'S',
  },
  { id: 's2', text: 'I notice small, practical details that other people miss.', pole: 'S' },
  {
    id: 's3',
    text: "I'd rather follow a proven method than experiment with a new one.",
    pole: 'S',
  },
  {
    id: 's4',
    text: 'I care more about what is actually true right now than what could be.',
    pole: 'S',
  },
  // Intuition
  {
    id: 'n1',
    text: "I'm more interested in the underlying pattern than the raw data.",
    pole: 'N',
  },
  {
    id: 'n2',
    text: 'I often catch myself imagining how things could be different.',
    pole: 'N',
  },
  {
    id: 'n3',
    text: 'Abstract ideas excite me more than step-by-step instructions.',
    pole: 'N',
  },
  {
    id: 'n4',
    text: 'I connect dots between unrelated things without really trying.',
    pole: 'N',
  },
  // Thinking
  {
    id: 't1',
    text: 'When I make a decision, logic wins over how people will feel about it.',
    pole: 'T',
  },
  { id: 't2', text: "I'd rather be told the honest truth than a kind one.", pole: 'T' },
  {
    id: 't3',
    text: 'I look for the flaw in an argument before I look for its merit.',
    pole: 'T',
  },
  { id: 't4', text: 'Being consistent matters more to me than being warm.', pole: 'T' },
  // Feeling
  {
    id: 'f1',
    text: "How a decision affects people matters as much to me as whether it's correct.",
    pole: 'F',
  },
  {
    id: 'f2',
    text: 'I find it hard to stay neutral when someone I care about is upset.',
    pole: 'F',
  },
  {
    id: 'f3',
    text: "I'd soften the truth if the full version would hurt someone unnecessarily.",
    pole: 'F',
  },
  {
    id: 'f4',
    text: 'Keeping harmony in a group feels more important than winning the argument.',
    pole: 'F',
  },
  // Judging
  {
    id: 'j1',
    text: 'I feel better once a decision is made and I can stop weighing options.',
    pole: 'J',
  },
  {
    id: 'j2',
    text: 'I like having a plan for the day rather than seeing what happens.',
    pole: 'J',
  },
  { id: 'j3', text: "Unfinished tasks nag at me until they're closed out.", pole: 'J' },
  {
    id: 'j4',
    text: "I'd rather book things in advance than decide at the last minute.",
    pole: 'J',
  },
  // Perceiving
  {
    id: 'p1',
    text: "I'd rather keep my options open than commit to a fixed plan.",
    pole: 'P',
  },
  {
    id: 'p2',
    text: 'Deadlines are something I work toward at the last minute, not ahead of time.',
    pole: 'P',
  },
  {
    id: 'p3',
    text: 'New information changing my plans excites me more than it stresses me.',
    pole: 'P',
  },
  {
    id: 'p4',
    text: 'I like starting things more than I like finishing the paperwork on them.',
    pole: 'P',
  },
];

export const MBTI_TYPE_BLURBS: Record<string, { name: string; blurb: string }> = {
  INTJ: {
    name: 'The Architect',
    blurb:
      'Strategic and independent, drawn to long-range plans and systems that actually work, more comfortable improving a model than making small talk about it.',
  },
  INTP: {
    name: 'The Logician',
    blurb:
      'Curious and analytical, happiest pulling an idea apart to see how it really works, often more interested in the theory than in applying it.',
  },
  ENTJ: {
    name: 'The Commander',
    blurb:
      'Decisive and organized, naturally steps into leading a plan toward a clear goal, direct about what needs to happen next.',
  },
  ENTP: {
    name: 'The Debater',
    blurb:
      'Quick, curious, and argumentative in the best way, energized by exploring an idea from every possible angle before settling on one.',
  },
  INFJ: {
    name: 'The Advocate',
    blurb:
      "Idealistic and insightful, drawn to meaning and other people's inner lives, quietly persistent about causes that matter.",
  },
  INFP: {
    name: 'The Mediator',
    blurb:
      'Values-driven and imaginative, guided by a strong internal sense of right and wrong, more at ease writing it down than saying it out loud.',
  },
  ENFJ: {
    name: 'The Protagonist',
    blurb:
      "Warm and persuasive, genuinely energized by helping people grow, often the one who notices what a group needs before it's said.",
  },
  ENFP: {
    name: 'The Campaigner',
    blurb:
      'Enthusiastic and people-focused, sees possibility everywhere, happiest when a conversation or a project is still wide open.',
  },
  ISTJ: {
    name: 'The Logistician',
    blurb:
      "Reliable and detail-oriented, trusts what's proven over what's trendy, the person a plan can actually depend on.",
  },
  ISFJ: {
    name: 'The Defender',
    blurb:
      'Quietly dependable and attentive, remembers what people need before they ask, most comfortable supporting from close up.',
  },
  ESTJ: {
    name: 'The Executive',
    blurb:
      'Organized and direct, turns a plan into a checklist and the checklist into results, not shy about saying what should happen.',
  },
  ESFJ: {
    name: 'The Consul',
    blurb:
      'Warm and sociable, keeps a group running smoothly, genuinely invested in the people immediately around them.',
  },
  ISTP: {
    name: 'The Virtuoso',
    blurb:
      'Practical and unflappable, learns best by taking something apart, prefers doing to discussing.',
  },
  ISFP: {
    name: 'The Adventurer',
    blurb:
      'Gentle and spontaneous, quietly guided by personal values, expresses more through action or craft than through words.',
  },
  ESTP: {
    name: 'The Entrepreneur',
    blurb:
      'Bold and present-focused, reads a room fast and acts on it, more comfortable improvising than following a script.',
  },
  ESFP: {
    name: 'The Entertainer',
    blurb:
      'Spontaneous and warm, brings energy into a room without trying, lives more in the moment than in the plan.',
  },
};
